/**
 * Author: Frontend Team + Claude Code
 * Generation: Real Animation Chain Test (Engine-Centric + Combat)
 * Version: v3.1.1-backend
 * Modified: 2025-09-18
 *
 * 🔧 Engine-Centric Architecture Update:
 * - Updated to match SinglePathAnimationEngine integration
 * - All animations now route through Engine.handle() priority queue
 * - SceneAdapter spawn parameter support
 * - Removed direct BattleScene animation processing
 *
 * ⚔️ Combat Test Integration (Events 4-7):
 * - Added complete combat sequence testing
 * - 3-step workflow: Spawn → Move Adjacent → Combat
 * - Pure event-driven, no hardcoded implementation
 * - Tests COMBAT_STARTED, DAMAGE_DEALT, ATTACK_MISSED, COMBAT_ENDED
 * - Theater mode integration
 */

import React, { useEffect, useRef, useState } from 'react';
import * as Phaser from 'phaser';
import { BattleScene } from '../game/scenes/BattleScene';
import webSocketService from '../services/websocket/WebSocketService';

interface ChainLog {
  timestamp: string;
  module: string;
  method: string;
  details: string;
  status: 'success' | 'error' | 'info' | 'warning';
  agentId?: string;
}

const buttonStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '12px',
  margin: '5px 0',
  border: 'none',
  borderRadius: '5px',
  color: 'white',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold'
};

const smallButtonStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: 'none',
  borderRadius: '3px',
  backgroundColor: '#2980B9',
  color: 'white',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 'bold'
};

const AgentSpawnedRealTest: React.FC = () => {
  const gameRef = useRef<Phaser.Game | null>(null);
  const sceneRef = useRef<BattleScene | null>(null);
  const [logs, setLogs] = useState<ChainLog[]>([]);
  const [gameReady, setGameReady] = useState(false);
  const [testRunning, setTestRunning] = useState(false);
  const [socketUrl, setSocketUrl] = useState<string>(() => {
    try { return webSocketService.getConfig().url; } catch { return 'ws://localhost:8080'; }
  });
  const engineMode = /:(8081)\b/.test(socketUrl || ''); // 连接到GameEngine专用WS端口时启用引擎命令
  const [connected, setConnected] = useState(false);
  const [lastAgentId, setLastAgentId] = useState<string | null>(null);
  const agentPositionsRef = useRef<Map<string, {x:number,y:number}>>(new Map());
  const backendAgentRegisteredRef = useRef<boolean>(false);
  const lastSpawnRef = useRef<{ itemId: string; category: 'weapon'|'medical'; weaponType?: 'SWORD'|'AXE'|'LANCE'; medicalType?: 'large'|'small'; pos:{x:number,y:number} }|null>(null);
  const [inventory, setInventory] = useState<Record<string, { equipped?: string|null; weapons: string[]; medical: { small: number; large: number } }>>({});
  const [cameraControls, setCameraControls] = useState({
    x: 0,
    y: 0,
    zoom: 1
  });
  const [testAgent, setTestAgent] = useState({
    id: 'backend_agent',
    position: { x: 5, y: 5 },
    color: 'blue',
    agentType: 'agent',
    health: 100
  });
  const [movementPath, setMovementPath] = useState<{x: number, y: number}[]>([]);
  const [currentPathIndex, setCurrentPathIndex] = useState(0);
  const [isMoving, setIsMoving] = useState(false);
  const [weaponSelect, setWeaponSelect] = useState<'SWORD'|'AXE'|'LANCE'>('SWORD');
  const [medicalSelect, setMedicalSelect] = useState<'small'|'large'>('large');
  const [medicalIds, setMedicalIds] = useState<string[]>([]);
  const [selectedMedicalId, setSelectedMedicalId] = useState<string>('');

  // 🔥 Combat Test States
  const [combatAgents, setCombatAgents] = useState({
    attacker: {
      id: 'combat_agent_001_sword',
      position: { x: 3, y: 3 },
      color: 'red',
      health: 100,
      weapon: 'SWORD'
    },
    defender: {
      id: 'combat_agent_002_unarmed',
      position: { x: 7, y: 3 },
      color: 'blue',
      health: 100,
      weapon: null
    }
  });
  const [combatState, setCombatState] = useState({
    inProgress: false,
    combatId: '',
    currentTurn: 0,
    maxTurns: 10,
    winner: null as string | null
  });

  const addLog = (module: string, method: string, details: string, status: 'success' | 'error' | 'info' | 'warning' = 'info', agentId?: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, module, method, details, status, agentId }]);
  };

  // --- Helpers for resource tests ---
  const TILE = 24;
  const gridToPixel = (gx: number, gy: number) => ({ x: gx * TILE + TILE / 2, y: gy * TILE + TILE / 2 });
  const normalizeSocketUrl = (value: string): string | null => {
    try {
      const v = (value || '').trim();
      if (!v) return null;
      // If it already includes a scheme, validate via URL
      if (/^wss?:\/\//i.test(v) || /^https?:\/\//i.test(v)) {
        // Validate
        new URL(v);
        return v.replace(/^http:\/\//i, 'ws://').replace(/^https:\/\//i, 'wss://');
      }
      // If user typed host:port
      if (/^[\w.-]+:\d+$/.test(v)) return `ws://${v}`;
      // If user typed only port
      if (/^\d+$/.test(v)) return `ws://localhost:${v}`;
      // If only hostname
      if (/^[\w.-]+$/.test(v)) return `ws://${v}:8080`;
      return null;
    } catch {
      return null;
    }
  };
  // Backend mode: no-op placeholders to avoid accidental mock triggers
  const ensureAgentSpawned = (id: string, pos: {x:number,y:number}, color: string) => {
    addLog('BackendMode', 'ensureAgentSpawned', `No-op in backend mode. Awaiting backend AGENT_SPAWNED. (${id})`, 'info', id);
  };
  const triggerResourceSpawned = (_items: any[]) => {
    addLog('BackendMode', 'RESOURCE_SPAWNED', 'No-op in backend mode. Awaiting backend events.', 'info');
  };
  const triggerResourceCollected = (agentId: string, itemId: string, category: string, type: string) => {
    addLog('BackendMode', 'RESOURCE_COLLECTED', `No-op in backend mode. agent=${agentId} item=${itemId} (${category}/${type})`, 'info', agentId);
  };
  const triggerWeaponEquipped = (agentId: string, weaponType: string) => {
    addLog('BackendMode', 'WEAPON_EQUIPPED', `No-op in backend mode. agent=${agentId} weapon=${weaponType}`, 'info', agentId);
  };

  // Initialize Phaser Game with BattleScene
  useEffect(() => {
    console.log('🎮 AgentSpawnedRealTest: Initializing Phaser Game...');

    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: 1200,
      height: 800,
      parent: 'phaser-game-container',
      backgroundColor: '#2c3e50',
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 0, x: 0 },
          debug: false
        }
      },
      scene: BattleScene
    };

    gameRef.current = new Phaser.Game(config);

    // Get scene reference when ready
    gameRef.current.events.once('ready', () => {
      const scene = gameRef.current?.scene.getScene('BattleScene') as BattleScene;
      if (scene) {
        sceneRef.current = scene;
        addLog('PhaserGame', 'initialize', 'Phaser Game and BattleScene initialized successfully', 'success');
        setGameReady(true);
      } else {
        addLog('PhaserGame', 'initialize', 'Failed to get BattleScene reference', 'error');
      }
    });

    return () => {
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
      }
    };
  }, []);

  // Setup WebSocket and enhanced logging
  useEffect(() => {
    if (!gameReady || !sceneRef.current) return;

    addLog('WebSocketService', 'setup', 'Setting up WebSocket connection and chain logging', 'info');
    // Backend mode: ensure mock mode disabled and connect
    webSocketService.disableMockMode();
    addLog('WebSocketService', 'mockMode', 'Mock mode disabled (backend mode)', 'info');

    // Bind registry for bridge conversions (tileSize)
    const scene = sceneRef.current;
    webSocketService.setGameRegistry(scene.registry);

    // 不在此处自动连接，改由“Connect”按钮进行显式连接，避免输入未完成导致解析错误

    // Enhanced logging by intercepting key methods
    

    // Intercept BattleScene.handleAgentSpawnedEvent (Engine-centric routing)
    const originalHandleAgentSpawned = scene['handleAgentSpawnedEvent'];
    if (originalHandleAgentSpawned) {
      scene['handleAgentSpawnedEvent'] = function(data: any) {
        addLog('BattleScene', 'handleAgentSpawnedEvent', `🔧 Engine-centric: AGENT_SPAWNED for ${data?.agent?.id} → SinglePathAnimationEngine.handle()`, 'info', data?.agent?.id);
        return originalHandleAgentSpawned.call(this, data);
      };
    }

    // Monitor SceneAdapter agent creation (called via Engine → SceneAdapter.idle with spawn=true)
    const originalAddAgent = scene.addAgent;
    if (originalAddAgent) {
      scene.addAgent = function(id: string, gridX: number, gridY: number) {
        addLog('SceneAdapter', 'createAgent', `🔧 Engine → SceneAdapter.idle({spawn:true}) → PhaserAnimationManager.addAgent(${id}) at grid(${gridX}, ${gridY})`, 'info', id);
        const result = originalAddAgent.call(this, id, gridX, gridY);
        if (result) {
          addLog('SceneAdapter', 'createAgent', `✅ Agent container created successfully for ${id}`, 'success', id);
        } else {
          addLog('SceneAdapter', 'createAgent', `❌ Failed to create agent container for ${id}`, 'error', id);
        }
        return result;
      };
    }

    // Enhanced logging for console messages from animation modules
    const originalConsoleLog = console.log;
    console.log = (...args) => {
      const message = args.join(' ');

      // SinglePathAnimationEngine logs
      if (message.includes('🎬 SinglePathAnimationEngine')) {
        addLog('SinglePathAnimationEngine', 'handle', message.replace('🎬 SinglePathAnimationEngine: ', ''), 'info');
      }
      // SceneAdapter logs - enhanced to capture spawn parameters
      else if (message.includes('🎭 SinglePathAnimationEngine: Scene route')) {
        addLog('SceneAdapter', 'route', message.replace('🎭 SinglePathAnimationEngine: Scene route → ', ''), 'info');
      }
      // SceneAdapter spawn logs
      else if (message.includes('🎭 SceneAdapter: 创建Agent')) {
        addLog('SceneAdapter', 'idle-spawn', message.replace('🎭 SceneAdapter: ', ''), 'success');
      }
      // SceneAdapter item usage logs
      else if (message.includes('🧪 SceneAdapter:')) {
        addLog('SceneAdapter', 'use', message.replace('🧪 SceneAdapter: ', ''), 'info');
      }
      // Engine queue delay logs
      else if (message.includes('⏰ Engine: 延迟')) {
        addLog('Engine', 'queue-delay', message.replace('⏰ Engine: ', ''), 'info');
      }
      // PhaserAnimationManager logs
      else if (message.includes('PhaserAnimationManager')) {
        addLog('PhaserAnimationManager', 'action', message, 'info');
      }
      // SpriteAnimationPlayer logs
      else if (message.includes('SpriteAnimationPlayer')) {
        addLog('SpriteAnimationPlayer', 'action', message, 'info');
      }
      // Asset loading logs
      else if (message.includes('SpriteAssetLoader')) {
        addLog('SpriteAssetLoader', 'action', message, 'info');
      }

      originalConsoleLog(...args);
    };

    // Local subscriptions for tracking agent positions (for camera centering)
    const onSpawn = (d:any) => {
      const agent = d?.agent || d;
      if (agent?.id && agent?.position) {
        agentPositionsRef.current.set(agent.id, { x: agent.position.x, y: agent.position.y });
        setLastAgentId(agent.id);
        setTestAgent(prev => ({ ...prev, id: agent.id, position: agent.position }));
        addLog('Bridge', 'AGENT_SPAWNED', `agent=${agent.id} @(${agent.position.x},${agent.position.y})`, 'success', agent.id);
        // 标记：后端已登记该Agent（禁止再次发起agent:spawned以免重复创建容器）
        backendAgentRegisteredRef.current = true;
      }
    };
    const onMoved = (d:any) => {
      const id = d?.agentId;
      const pos = d?.toPosition || d?.position;
      if (id && pos) {
        agentPositionsRef.current.set(id, { x: pos.x, y: pos.y });
        setLastAgentId(id);
        setTestAgent(prev => ({ ...prev, id, position: pos }));
        addLog('Bridge', 'AGENT_MOVED', `${id} → (${pos.x},${pos.y})`, 'info', id);
      }
    };
    const onResource = (type:string) => (d:any) => {
      addLog('Bridge', type, JSON.stringify(d), 'info');
      if (type === 'RESOURCE_COLLECTED') {
        const agentId = d?.agentId;
        if (agentId) {
          setInventory(prev => {
            const inv = prev[agentId] || { equipped: null, weapons: [], medical: { small: 0, large: 0 } };
            const next = { ...prev } as any;
            const isWeapon = (d?.type || d?.item?.category || '').toString().toUpperCase() === 'WEAPON';
            if (isWeapon) {
              const wt = (d?.weaponType || d?.item?.weaponType || d?.item?.type || 'UNKNOWN').toString();
              next[agentId] = { ...inv, weapons: inv.weapons.concat([wt]) };
            } else {
              const m = (d?.medicalType || d?.item?.medicalType || d?.item?.type || '').toString().toLowerCase();
              const med = { ...inv.medical };
              if (m === 'small') med.small += 1; else if (m === 'large') med.large += 1;
              next[agentId] = { ...inv, medical: med };
            }
            return next;
          });
          // 记录真实采集的医疗itemId列表（当前测试agent）
          const cat = String(d?.item?.category || d?.type || '').toLowerCase();
          if ((cat === 'medical' || cat === 'consumable') && d?.itemId && agentId === testAgent.id) {
            const iid = String(d.itemId);
            setMedicalIds(prev => (prev.includes(iid) ? prev : prev.concat([iid])));
            setSelectedMedicalId(iid);
          }
        }
      }
      if (type === 'WEAPON_EQUIPPED') {
        // 仅用于动画/日志，不驱动面板状态（面板仅根据 weapons[] 是否非空显示“是否持有武器”）
      }
    };
    const onCombat = (d:any) => addLog('Bridge', 'COMBAT_*', JSON.stringify(d), 'info');
    webSocketService.onGameEvent('AGENT_SPAWNED', onSpawn);
    webSocketService.onGameEvent('AGENT_MOVED', onMoved);
    webSocketService.onGameEvent('RESOURCE_SPAWNED', onResource('RESOURCE_SPAWNED'));
    webSocketService.onGameEvent('RESOURCE_COLLECTED', onResource('RESOURCE_COLLECTED'));
    webSocketService.onGameEvent('WEAPON_EQUIPPED', onResource('WEAPON_EQUIPPED'));
    const onItemUsed = (d:any)=> addLog('Bridge','ITEM_USED', JSON.stringify(d),'info');
    const onItemConsumed = (d:any)=>{
      addLog('Bridge','ITEM_CONSUMED', JSON.stringify(d),'info');
      const agentId = d?.agentId;
      if (!agentId) return;
      const cat = String(d?.item?.category || '').toLowerCase();
      if (cat === 'medical') {
        const m = String(d?.item?.type || '').toLowerCase();
        setInventory(prev => {
          const inv = prev[agentId] || { equipped: null, weapons: [], medical: { small: 0, large: 0 } };
          const med = { ...inv.medical };
          if (m === 'small' && med.small > 0) med.small -= 1;
          if (m === 'large' && med.large > 0) med.large -= 1;
          return { ...prev, [agentId]: { ...inv, medical: med } };
        });
      }
    };
    const onHealthChanged = (d:any)=>{
      addLog('Bridge','AGENT_HEALTH_CHANGED', JSON.stringify(d),'info');
      if (d?.agentId === testAgent.id && d?.currentHealth) {
        setTestAgent(prev => ({ ...prev, health: d.currentHealth.current }));
        try {
          // 将健康变化路由到 BattleScene 的血条刷新（仅测试观察）
          const scene: any = sceneRef.current;
          scene?.updateAgentHealthBar?.(d.agentId, d.currentHealth);
        } catch {}
      }
    };
    webSocketService.onGameEvent('ITEM_USED', onItemUsed);
    webSocketService.onGameEvent('ITEM_CONSUMED', onItemConsumed);
    webSocketService.onGameEvent('AGENT_HEALTH_CHANGED', onHealthChanged);
    webSocketService.onGameEvent('COMBAT_STARTED', onCombat);
    webSocketService.onGameEvent('COMBAT_DAMAGE_DEALT', onCombat);
    webSocketService.onGameEvent('COMBAT_ATTACK_MISSED', onCombat);
    webSocketService.onGameEvent('COMBAT_ENDED', onCombat);

    return () => {
      console.log = originalConsoleLog;
      webSocketService.off('AGENT_SPAWNED', onSpawn);
      webSocketService.off('AGENT_MOVED', onMoved);
      webSocketService.off('RESOURCE_SPAWNED', onResource('RESOURCE_SPAWNED'));
      webSocketService.off('RESOURCE_COLLECTED', onResource('RESOURCE_COLLECTED'));
      webSocketService.off('WEAPON_EQUIPPED', onResource('WEAPON_EQUIPPED'));
      webSocketService.off('COMBAT_STARTED', onCombat);
      webSocketService.off('ITEM_USED', onItemUsed);
      webSocketService.off('ITEM_CONSUMED', onItemConsumed);
      webSocketService.off('AGENT_HEALTH_CHANGED', onHealthChanged);
      webSocketService.off('COMBAT_DAMAGE_DEALT', onCombat);
      webSocketService.off('COMBAT_ATTACK_MISSED', onCombat);
      webSocketService.off('COMBAT_ENDED', onCombat);
    };
  }, [gameReady]);

  // 键盘控制
  useEffect(() => {
    if (!gameReady) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      const moveSpeed = 150;
      const zoomSpeed = 0.3;

      switch (event.key.toLowerCase()) {
        case 'w':
        case 'arrowup':
          moveCamera(0, -moveSpeed);
          break;
        case 's':
        case 'arrowdown':
          moveCamera(0, moveSpeed);
          break;
        case 'a':
        case 'arrowleft':
          moveCamera(-moveSpeed, 0);
          break;
        case 'd':
        case 'arrowright':
          moveCamera(moveSpeed, 0);
          break;
        case 'q':
          zoomCamera(-zoomSpeed);
          break;
        case 'e':
          zoomCamera(zoomSpeed);
          break;
        case 'r':
          resetCamera();
          break;
        case 'f':
          centerOnAgent();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameReady, testAgent.position]);

  const triggerAgentSpawned = () => {
    if (!gameReady || testRunning) return;

    setTestRunning(true);
    setLogs([]); // Clear previous logs

    addLog('Test', 'triggerAgentSpawned', `Starting AGENT_SPAWNED test for ${testAgent.id}`, 'info', testAgent.id);

    // Create the exact WebSocket message format
    const agentSpawnedMessage = {
      type: 'AGENT_SPAWNED',
      data: {
        agent: {
          id: testAgent.id,
          position: testAgent.position,
          color: testAgent.color,
          health: testAgent.health
        }
      }
    };

    addLog('WebSocketService', 'triggerMockEvent', `Sending AGENT_SPAWNED message: ${JSON.stringify(agentSpawnedMessage.data)}`, 'info', testAgent.id);

    // Trigger the real WebSocket event
    webSocketService.triggerMockEvent('AGENT_SPAWNED', agentSpawnedMessage.data);

    // Monitor test completion and center camera
    setTimeout(() => {
      addLog('Test', 'completion', 'AGENT_SPAWNED test sequence completed', 'success', testAgent.id);
      setTestRunning(false);

      // Auto-center camera on the spawned agent
      setTimeout(() => {
        centerOnAgent();
      }, 500);
    }, 2000);
  };

  const clearLogs = () => {
    setLogs([]);
  };

  // 🔄 重置战斗状态函数
  const resetCombatState = () => {
    addLog('CombatEngine', 'resetState', '🔄 手动重置战斗状态', 'info');
    setCombatState({
      inProgress: false,
      combatId: '',
      currentTurn: 0,
      maxTurns: 10,
      winner: null
    });
  };

  const generateNewAgent = () => {
    const agentNumber = Math.floor(Math.random() * 1000);
    const colors = ['blue', 'red', 'green', 'purple', 'orange'];
    const randomColor = colors[Math.floor(Math.random() * colors.length)];
    const randomX = Math.floor(Math.random() * 15) + 1; // 扩大地图范围
    const randomY = Math.floor(Math.random() * 15) + 1;

    setTestAgent({
      id: `test_agent_${agentNumber}`,
      position: { x: randomX, y: randomY },
      color: randomColor,
      health: 100
    });
  };

  // 相机控制函数
  const moveCamera = (deltaX: number, deltaY: number) => {
    if (!sceneRef.current) return;

    const camera = sceneRef.current.cameras.main;
    const newX = camera.scrollX + deltaX;
    const newY = camera.scrollY + deltaY;

    camera.setScroll(newX, newY);
    setCameraControls({
      x: newX,
      y: newY,
      zoom: camera.zoom
    });

    addLog('Camera', 'moveCamera', `Moved to (${newX.toFixed(0)}, ${newY.toFixed(0)})`, 'info');
  };

  const zoomCamera = (delta: number) => {
    if (!sceneRef.current) return;

    const camera = sceneRef.current.cameras.main;
    const newZoom = Math.max(0.3, Math.min(3, camera.zoom + delta));

    camera.setZoom(newZoom);
    setCameraControls(prev => ({
      ...prev,
      zoom: newZoom
    }));

    addLog('Camera', 'zoomCamera', `Zoom: ${newZoom.toFixed(2)}x`, 'info');
  };

  const centerOnAgent = () => {
    if (!sceneRef.current) return;

    const camera = sceneRef.current.cameras.main;
    const targetId = lastAgentId || testAgent.id;
    const pos = agentPositionsRef.current.get(targetId) || testAgent.position;
    const pixel = gridToPixel(pos.x, pos.y);
    camera.centerOn(pixel.x, pixel.y);
    setCameraControls({
      x: camera.scrollX,
      y: camera.scrollY,
      zoom: camera.zoom
    });

    addLog('Camera', 'centerOnAgent', `Centered on ${targetId} at grid(${pos.x}, ${pos.y})`, 'success', targetId);
  };

  // 将当前测试agent的坐标同步到后端（通过 dev:inject → EventDispatcher → MapManager）
  const syncAgentPositionToBackend = async () => {
    try {
      const id = testAgent.id;
      const pos = { ...testAgent.position };
      // 只有在“尚未从后端收到AGENT_SPAWNED”的情况下，才注入spawn；
      // 否则只注入moved，避免重复创建Agent容器
      const known = agentPositionsRef.current.has(id) || backendAgentRegisteredRef.current;
      if (!known) {
        const data = { agent: { id, position: pos, color: testAgent.color, health: testAgent.health } };
        await webSocketService.emit('dev:inject', { eventType: 'agent:spawned', data });
        addLog('Dev','emit',`agent:spawned (sync) ${JSON.stringify(data)}`,'success', id);
        backendAgentRegisteredRef.current = true;
      } else {
        const from = agentPositionsRef.current.get(id) || pos;
        const data = { agentId: id, fromPosition: from, toPosition: pos };
        await webSocketService.emit('dev:inject', { eventType: 'agent:moved', data });
        addLog('Dev','emit',`agent:moved (sync) ${JSON.stringify(data)}`,'success', id);
      }
    } catch (e:any) {
      addLog('Dev','emit',`agent sync failed ${e?.message||e}`,'error');
    }
  };

  const resetCamera = () => {
    if (!sceneRef.current) return;

    const camera = sceneRef.current.cameras.main;
    camera.setScroll(0, 0);
    camera.setZoom(1);

    setCameraControls({ x: 0, y: 0, zoom: 1 });
    addLog('Camera', 'resetCamera', 'Camera reset to origin', 'info');
  };

  // LLM路径规划算法模拟
  const generateLLMPath = (from: {x: number, y: number}, to: {x: number, y: number}): {x: number, y: number}[] => {
    const path: {x: number, y: number}[] = [];
    let current = { ...from };

    // 模拟A*算法生成的路径点序列
    while (current.x !== to.x || current.y !== to.y) {
      path.push({ ...current });

      // 优先移动到目标点 (简化的曼哈顿距离启发式)
      if (current.x < to.x) current.x++;
      else if (current.x > to.x) current.x--;
      else if (current.y < to.y) current.y++;
      else if (current.y > to.y) current.y--;
    }

    path.push({ ...to }); // 添加终点
    return path;
  };

  // 生成复杂路径（模拟LLM战略决策）
  const generateStrategicPath = (agentPos: {x: number, y: number}): {x: number, y: number}[] => {
    // 模拟LLM决策：先去收集资源，然后移动到战略位置
    const resourcePos = {
      x: Math.floor(Math.random() * 12) + 2,
      y: Math.floor(Math.random() * 12) + 2
    };
    const strategicPos = {
      x: Math.floor(Math.random() * 10) + 3,
      y: Math.floor(Math.random() * 10) + 3
    };

    // 生成多段路径：当前位置 → 资源位置 → 战略位置
    const pathToResource = generateLLMPath(agentPos, resourcePos);
    const pathToStrategic = generateLLMPath(resourcePos, strategicPos);

    // 合并路径，移除重复点
    const fullPath = [...pathToResource];
    pathToStrategic.slice(1).forEach(point => fullPath.push(point));

    return fullPath;
  };

  // ---- Resource Test Scenarios (8/9/10) ----
  const testCollectMedical = () => {
    const agentId = testAgent.id;
    const agentPos = { ...testAgent.position };
    const medicalTile = { x: agentPos.x + 2, y: agentPos.y }; // 在前方两格
    const itemId = `med_${Date.now()}`;
    ensureAgentSpawned(agentId, agentPos, testAgent.color);
    // 1) 生成医疗资源
    triggerResourceSpawned([{ itemId, type: 'CONSUMABLE', category: 'medical', medicalType: 'large', position: gridToPixel(medicalTile.x, medicalTile.y) }]);
    // 2) 路径移动到资源格
    const path = generateLLMPath(agentPos, medicalTile);
    executeMovementPath(path);
    // 3) 收集（无动画、无equipped）
    setTimeout(() => {
      triggerResourceCollected(agentId, itemId, 'consumable', 'large');
    }, 600);
  };

  const testCollectWeaponAutoEquip = () => {
    const agentId = testAgent.id;
    const agentPos = { ...testAgent.position };
    const weaponTile = { x: agentPos.x, y: agentPos.y + 2 }; // 下方两格
    const itemId = `wpn_${Date.now()}`;
    const weaponType = 'SWORD';
    ensureAgentSpawned(agentId, agentPos, testAgent.color);
    // 1) 生成武器
    triggerResourceSpawned([{ itemId, type: 'WEAPON', category: 'weapon', weaponType: weaponType as any, position: gridToPixel(weaponTile.x, weaponTile.y) }]);
    // 2) 移动到资源格
    const path = generateLLMPath(agentPos, weaponTile);
    executeMovementPath(path);
    // 3) 收集（播放一次拾取反馈动画由引擎决定），随后自动装备
    setTimeout(() => {
      triggerResourceCollected(agentId, itemId, 'weapon', weaponType);
      setTimeout(() => triggerWeaponEquipped(agentId, weaponType), 120);
    }, 600);
  };

  const testOrderTolerance = () => {
    const agentId = testAgent.id;
    const agentPos = { ...testAgent.position };
    const weaponTile = { x: agentPos.x + 3, y: agentPos.y + 1 };
    const itemId = `wpn_${Date.now()}`;
    const weaponType = 'AXE';
    ensureAgentSpawned(agentId, agentPos, testAgent.color);
    triggerResourceSpawned([{ itemId, type: 'WEAPON', category: 'weapon', weaponType: weaponType as any, position: gridToPixel(weaponTile.x, weaponTile.y) }]);
    const path = generateLLMPath(agentPos, weaponTile);
    executeMovementPath(path);
    // 几乎同时到达：收集后很快下发 WEAPON_EQUIPPED
    setTimeout(() => {
      triggerResourceCollected(agentId, itemId, 'weapon', weaponType);
      setTimeout(() => triggerWeaponEquipped(agentId, weaponType), 50);
    }, 600);
  };

  // 生成全量资源样本：small/large medkit + axe/sword/lance
  const spawnAllResourceSamples = () => {
    const base = { x: testAgent.position.x + 2, y: testAgent.position.y };
    const items = [
      { itemId: `med_small_${Date.now()}`, type: 'CONSUMABLE' as const, category: 'medical' as const, medicalType: 'small' as const, position: gridToPixel(base.x, base.y) },
      { itemId: `med_large_${Date.now()+1}`, type: 'CONSUMABLE' as const, category: 'medical' as const, medicalType: 'large' as const, position: gridToPixel(base.x+1, base.y) },
      { itemId: `wpn_sword_${Date.now()+2}`, type: 'WEAPON' as const, category: 'weapon' as const, weaponType: 'SWORD' as const, position: gridToPixel(base.x, base.y+1) },
      { itemId: `wpn_axe_${Date.now()+3}`, type: 'WEAPON' as const, category: 'weapon' as const, weaponType: 'AXE' as const, position: gridToPixel(base.x+1, base.y+1) },
      { itemId: `wpn_lance_${Date.now()+4}`, type: 'WEAPON' as const, category: 'weapon' as const, weaponType: 'LANCE' as const, position: gridToPixel(base.x+2, base.y+1) }
    ];
    triggerResourceSpawned(items);
  };

  // 执行路径序列移动
  const executeMovementPath = (path: {x: number, y: number}[]) => {
    if (path.length < 2) return;

    setMovementPath(path);
    setCurrentPathIndex(0);
    setIsMoving(true);

    addLog('LLMPathPlanner', 'generatePath', `Generated ${path.length}-step movement path: ${path.map(p => `(${p.x},${p.y})`).join(' → ')}`, 'info', testAgent.id);

    // 开始逐步移动
    executeNextMovement(path, 0);
  };

  const executeNextMovement = (path: {x: number, y: number}[], index: number) => {
    if (index >= path.length - 1) {
      setIsMoving(false);
      setCurrentPathIndex(0);
      addLog('MovementSequence', 'completed', 'Movement path sequence completed', 'success', testAgent.id);
      return;
    }

    const from = path[index];
    const to = path[index + 1];

    // 创建真实的AGENT_MOVED事件
    const moveEvent = {
      agentId: testAgent.id,
      fromPosition: from,
      toPosition: to,
      turnNumber: index + 1,
      timestamp: new Date().toISOString(),
      // 扩展：添加路径上下文信息
      pathContext: {
        currentStep: index + 1,
        totalSteps: path.length - 1,
        remainingPath: path.slice(index + 2),
        pathType: 'strategic_movement'
      }
    };

    addLog('LLMDecision', 'executeMovement', `Step ${index + 1}/${path.length - 1}: (${from.x},${from.y}) → (${to.x},${to.y})`, 'info', testAgent.id);

    // 触发真实的AGENT_MOVED事件
    webSocketService.triggerMockEvent('AGENT_MOVED', moveEvent);

    // 更新测试agent位置
    setTestAgent(prev => ({ ...prev, position: to }));
    setCurrentPathIndex(index + 1);

    // 🔧 不添加任何人工延迟 - 完全依赖生产代码的时序控制
    // Engine的stepInterval + MovementAnimationSync的动画时长会自然控制时序
    executeNextMovement(path, index + 1);
  };

  // 🔥 Combat Test Functions - Pure Event Driven, No Implementation
  const spawnCombatAgents = () => {
    if (combatState.inProgress) return;

    addLog('CombatTest', 'spawnAgents', 'Step 1: Spawning combat test agents with proper timing', 'info');

    // Spawn attacker with sword at (3,3)
    webSocketService.triggerMockEvent('AGENT_SPAWNED', {
      agent: {
        id: combatAgents.attacker.id,
        position: combatAgents.attacker.position,
        color: combatAgents.attacker.color,
        health: combatAgents.attacker.health
      }
    });

    // 🔧 等待Agent创建完成后再装备武器 - 避免时序冲突
    setTimeout(() => {
      // 检查sprite是否存在，如果不存在则继续等待
      const checkSpriteAndEquipWeapon = () => {
        const pma = sceneRef.current?.animationManager;
        if (pma && pma.agentContainers.has(combatAgents.attacker.id)) {
          addLog('CombatTest', 'equipWeapon', `✅ Agent sprite已创建，开始装备武器: ${combatAgents.attacker.id}`, 'info');
          webSocketService.triggerMockEvent('WEAPON_EQUIPPED', {
            agentId: combatAgents.attacker.id,
            weaponId: 'sword_001',
            weaponType: 'SWORD',
            previousWeapon: null
          });
        } else {
          addLog('CombatTest', 'waitingForSprite', `⏳ Agent sprite尚未创建，继续等待: ${combatAgents.attacker.id}`, 'warning');
          setTimeout(checkSpriteAndEquipWeapon, 1000); // 再等1秒后检查
        }
      };
      checkSpriteAndEquipWeapon();
    }, 3000); // 初始等待3秒

    // Spawn defender without weapon at (7,3) - distant position
    setTimeout(() => {
      webSocketService.triggerMockEvent('AGENT_SPAWNED', {
        agent: {
          id: combatAgents.defender.id,
          position: combatAgents.defender.position,
          color: combatAgents.defender.color,
          health: combatAgents.defender.health
        }
      });
      addLog('CombatTest', 'agentsSpawned', `Attacker: ${combatAgents.attacker.id} @(3,3) SWORD, Defender: ${combatAgents.defender.id} @(7,3) UNARMED`, 'success');
    }, 500); // 稍微延迟第二个Agent避免同时创建
  };

  const moveToAdjacentForCombat = () => {
    if (combatState.inProgress) return;

    addLog('CombatTest', 'moveToAdjacent', 'Step 2: Moving attacker to adjacent position via MovementAnimationSync', 'info');

    // 🔧 使用生产代码的移动系统 - 调用AGENT_MOVED事件
    // MovementAnimationSync会处理所有移动动画和时序
    const fromPosition = combatAgents.attacker.position;
    const toPosition = { x: 6, y: 3 }; // Adjacent to defender at (7,3)

    // 触发真实的AGENT_MOVED事件，让生产代码处理
    webSocketService.triggerMockEvent('AGENT_MOVED', {
      agentId: combatAgents.attacker.id,
      fromPosition,
      toPosition,
      turnNumber: 1,
      timestamp: new Date().toISOString(),
      // 标记这是战斗前置移动
      combatPreparation: true
    });

    // 只更新本地状态用于UI显示，实际移动由生产代码处理
    setCombatAgents(prev => ({
      ...prev,
      attacker: { ...prev.attacker, position: toPosition }
    }));

    addLog('MovementAnimationSync', 'triggerMove', `${combatAgents.attacker.id}: (${fromPosition.x},${fromPosition.y}) → (${toPosition.x},${toPosition.y}) via Engine`, 'success');
  };

  const startCombatSequence = () => {
    console.log('🚀 startCombatSequence函数被调用 - DIRECT CONSOLE');
    addLog('CombatEngine', 'startCombatSequence', `🚀 startCombatSequence函数被调用`, 'info');
    console.log('🔍 状态检查 - DIRECT CONSOLE:', { gameReady, 'combatState.inProgress': combatState.inProgress });
    addLog('CombatEngine', 'statusCheck', `🔍 状态检查: gameReady=${gameReady}, combatState.inProgress=${combatState.inProgress}`, 'info');

    if (!gameReady || combatState.inProgress) {
      addLog('CombatEngine', 'earlyExit', `❌ 提前退出: gameReady=${gameReady}, combatState.inProgress=${combatState.inProgress}`, 'warning');
      return;
    }

    const combatId = `combat_${Date.now()}`;
    addLog('CombatEngine', 'generateCombatId', `🆔 生成战斗ID: ${combatId}`, 'info');

    addLog('CombatEngine', 'setCombatState', `📝 设置战斗状态为inProgress=true`, 'info');
    setCombatState(prev => ({
      ...prev,
      inProgress: true,
      combatId,
      currentTurn: 0,
      winner: null
    }));

    addLog('CombatEngine', 'startCombat', 'Step 3: Combat initiated between adjacent agents', 'info');

    // 1. Start combat
    try {
      console.log('📡 准备发送COMBAT_STARTED事件 - DIRECT CONSOLE');
      addLog('CombatEngine', 'sendCOMBAT_STARTED', `📡 后端发送: COMBAT_STARTED事件 combatId: ${combatId}`, 'info');

      // 🔍 详细日志：确认要发送的武器数据
      const combatStartedData = {
        combatId,
        attackerId: combatAgents.attacker.id,
        defenderId: combatAgents.defender.id,
        position: { x: 5, y: 3 }, // 战斗位置
        terrain: 'grass_vs_grass',
        startTime: new Date().toISOString(),
        attackerColor: combatAgents.attacker.color,
        defenderColor: combatAgents.defender.color,
        // 🔧 新增：完整武器信息
        attackerWeapon: {
          itemId: 'sword_001_' + combatAgents.attacker.id,
          type: 'SWORD',
          name: '基础剑',
          category: 'weapon',
          status: 'equipped',
          owner: combatAgents.attacker.id,
          properties: {
            attackPower: 15,
            durability: 100,
            maxDurability: 100
          }
        },
        defenderWeapon: {
          itemId: 'unarmed_' + combatAgents.defender.id,
          type: 'UNARMED',
          name: '徒手',
          category: 'weapon',
          status: 'equipped',
          owner: combatAgents.defender.id,
          properties: {
            attackPower: 5,
            durability: 999,
            maxDurability: 999
          }
        },
        attackerHealth: { current: combatAgents.attacker.health, max: combatAgents.attacker.health },
        defenderHealth: { current: combatAgents.defender.health, max: combatAgents.defender.health }
      };

      // 输出将要发送的数据
      console.log('📤 即将发送的COMBAT_STARTED数据:', combatStartedData);
      console.log('📤 攻击者武器详情:', combatStartedData.attackerWeapon);
      console.log('📤 防御者武器详情:', combatStartedData.defenderWeapon);

      webSocketService.triggerMockEvent('COMBAT_STARTED', combatStartedData);

      console.log('✅ COMBAT_STARTED事件发送成功 - DIRECT CONSOLE');

      // 2. 在真实系统中，后端会自动推送战斗事件
      // 这里我们模拟后端行为：在剧院模式激活后推送战斗事件
      console.log('⏰ 设置2秒延迟 - 等待剧院模式激活完成 - DIRECT CONSOLE');
      addLog('CombatEngine', 'scheduleFirstTurn', `⏰ 已设置2秒延迟，等待剧院模式激活完成 combatId: ${combatId}`, 'info');
      setTimeout(() => {
        console.log('⏰ 2秒延迟结束，开始模拟后端推送战斗事件 - DIRECT CONSOLE');
        addLog('CombatEngine', 'timeoutExecuted', `⏰ 2秒延迟结束，开始模拟后端推送战斗事件 combatId: ${combatId}`, 'info');

        // 🔧 正确做法：模拟后端推送战斗事件序列
        simulateBackendCombatEvents(combatId);
      }, 2000); // Theater setup delay

    } catch (error) {
      console.error('❌ startCombatSequence发生异常:', error);
      addLog('CombatEngine', 'error', `❌ 发生异常: ${error.message}`, 'error');
    }
  };

  // 🔧 正确的架构：模拟后端推送战斗事件序列
  const simulateBackendCombatEvents = (combatId: string) => {
    console.log('🎯 后端模拟器：开始推送战斗事件序列 - DIRECT CONSOLE');
    addLog('Backend', 'simulateEvents', `🎯 后端模拟器：开始推送战斗事件序列 combatId: ${combatId}`, 'info');

    // 模拟第一轮攻击：红色攻击者攻击蓝色防守者
    setTimeout(() => {
      console.log('📡 后端推送：COMBAT_DAMAGE_DEALT事件 - DIRECT CONSOLE');
      addLog('Backend', 'pushEvent', `📡 后端推送: COMBAT_DAMAGE_DEALT - 第1轮攻击`, 'info');
      webSocketService.triggerMockEvent('COMBAT_DAMAGE_DEALT', {
        combatId,
        attackerId: combatAgents.attacker.id,
        defenderId: combatAgents.defender.id,
        damage: 25,
        isCritical: false,
        weaponUsed: {
          itemId: 'sword_001_' + combatAgents.attacker.id,
          type: 'SWORD',
          name: '基础剑',
          category: 'weapon',
          status: 'equipped',
          owner: combatAgents.attacker.id,
          properties: {
            attackPower: 15,
            durability: 100,
            maxDurability: 100
          }
        }
      });
    }, 1000);

    // 模拟第二轮攻击：蓝色防守者反击
    setTimeout(() => {
      console.log('📡 后端推送：COMBAT_DAMAGE_DEALT事件 - 反击 - DIRECT CONSOLE');
      addLog('Backend', 'pushEvent', `📡 后端推送: COMBAT_DAMAGE_DEALT - 第2轮反击`, 'info');
      webSocketService.triggerMockEvent('COMBAT_DAMAGE_DEALT', {
        combatId,
        attackerId: combatAgents.defender.id,
        defenderId: combatAgents.attacker.id,
        damage: 20,
        isCritical: false,
        weaponUsed: {
          itemId: 'unarmed_' + combatAgents.defender.id,
          type: 'UNARMED',
          name: '徒手',
          category: 'weapon',
          status: 'equipped',
          owner: combatAgents.defender.id,
          properties: {
            attackPower: 5,
            durability: 999,
            maxDurability: 999
          }
        }
      });
    }, 4000);

    // 模拟第三轮攻击：攻击失手
    setTimeout(() => {
      console.log('📡 后端推送：COMBAT_ATTACK_MISSED事件 - DIRECT CONSOLE');
      addLog('Backend', 'pushEvent', `📡 后端推送: COMBAT_ATTACK_MISSED - 第3轮失手`, 'info');
      webSocketService.triggerMockEvent('COMBAT_ATTACK_MISSED', {
        combatId,
        attackerId: combatAgents.attacker.id,
        defenderId: combatAgents.defender.id
      });
    }, 7000);

    // 模拟战斗结束
    setTimeout(() => {
      console.log('📡 后端推送：COMBAT_ENDED事件 - DIRECT CONSOLE');
      addLog('Backend', 'pushEvent', `📡 后端推送: COMBAT_ENDED - 战斗结束`, 'info');
      webSocketService.triggerMockEvent('COMBAT_ENDED', {
        combatId,
        winner: combatAgents.attacker.id,
        loser: combatAgents.defender.id,
        reason: 'health_depleted'
      });
    }, 10000);
  };

  const endCombat = (combatId: string, winner: string, loser: string) => {
    addLog('CombatEngine', 'endCombat', `Combat ended. Winner: ${winner}, Loser: ${loser}`, 'success');

    addLog('CombatEngine', 'sendCOMBAT_ENDED', `📡 后端发送: COMBAT_ENDED - 胜者:${winner}, 败者:${loser}`, 'info');
    webSocketService.triggerMockEvent('COMBAT_ENDED', {
      combatId,
      winner: winner === 'draw' ? null : winner,
      loser: loser === 'draw' ? null : loser,
      reason: winner === 'draw' ? 'max_turns_reached' : 'health_depleted'
    });

    setCombatState(prev => ({
      ...prev,
      inProgress: false,
      winner: winner === 'draw' ? 'DRAW' : winner
    }));
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace', display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <h1>🎮 Backend E2E Test (Socket.IO → Engine → Render)</h1>

      {/* Game Status */}
      <div style={{ marginBottom: '20px' }}>
        <span style={{
          padding: '5px 10px',
          borderRadius: '5px',
          backgroundColor: gameReady ? '#90EE90' : '#FFB6C1',
          color: '#000',
          marginRight: '10px'
        }}>
          Phaser Game: {gameReady ? 'READY' : 'LOADING'}
        </span>
        <span style={{
          padding: '5px 10px',
          borderRadius: '5px',
          backgroundColor: connected ? '#90EE90' : '#FFB6C1',
          color: '#000',
          marginRight: '10px'
        }}>
          Socket: {connected ? 'CONNECTED' : 'DISCONNECTED'}
        </span>
        <span style={{
          padding: '5px 10px',
          borderRadius: '5px',
          backgroundColor: testRunning ? '#FFA500' : '#90EE90',
          color: '#000'
        }}>
          Test Status: {testRunning ? 'RUNNING' : 'IDLE'}
        </span>
        <span style={{
          padding: '5px 10px',
          borderRadius: '5px',
          backgroundColor: isMoving ? '#FF6B35' : '#95a5a6',
          color: '#000',
          marginLeft: '10px'
        }}>
          Movement: {isMoving ? `${currentPathIndex}/${movementPath.length - 1}` : 'IDLE'}
        </span>
        <span style={{
          padding: '5px 10px',
          borderRadius: '5px',
          backgroundColor: combatState.inProgress ? '#E74C3C' : (combatState.winner ? '#27AE60' : '#95a5a6'),
          color: '#000',
          marginLeft: '10px'
        }}>
          Combat: {combatState.inProgress ? `TURN ${combatState.currentTurn}` : (combatState.winner ? `ENDED` : 'IDLE')}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '20px', flex: 1 }}>

        {/* Control Panel */}
        <div style={{ maxHeight: '100vh', overflowY: 'auto' }}>
          <h2>🎯 Backend Controls</h2>

          <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #ccc', borderRadius: '5px' }}>
            <h3>🔌 Socket.IO Connection</h3>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
              <input
                value={socketUrl}
                onChange={e => setSocketUrl(e.target.value)}
                placeholder="ws://localhost:8080"
                style={{ flex: 1, padding: '6px', fontFamily: 'monospace', fontSize: '12px' }}
              />
              <button
                onClick={() => {
                  setConnected(false);
                  webSocketService.disconnect();
                  const normalized = normalizeSocketUrl(socketUrl);
                  if (!normalized) {
                    addLog('WebSocketService', 'connect', `无效地址: ${socketUrl}`, 'error');
                    return;
                  }
                  addLog('WebSocketService','connect','准备连接: '+normalized,'info');
                  webSocketService.connect(normalized)
                    .then(() => { setConnected(true); addLog('WebSocketService', 'connect', `Connected to ${normalized}`, 'success'); })
                    .catch(err => { setConnected(false); addLog('WebSocketService', 'connect', `Failed: ${err?.message||err}`, 'error'); });
                }}
                style={{...smallButtonStyle, backgroundColor: '#27AE60'}}
              >Connect</button>
              <button
                onClick={() => { webSocketService.disconnect(); setConnected(false); addLog('WebSocketService','disconnect','Disconnected','info'); }}
                style={{...smallButtonStyle, backgroundColor: '#C0392B'}}
              >Disconnect</button>
            </div>
            <div style={{ fontSize: '11px', color: '#666' }}>
              Backend emits colon-style events; bridge maps → internal uppercase events.
            </div>
        </div>

          {/* DEV: Backend Event Emitters */}
          <div style={{ marginBottom: '15px', padding: '12px', border: '2px dashed #888', borderRadius: '5px' }}>
            <h3>🧪 Dev Event Emitters (via backend)</h3>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px' }}>
              Emits a colon-style event to backend: backend relays → frontend.
            </div>

            {/* Agent controls */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
              <input placeholder="agentId" value={testAgent.id}
                     onChange={e=>setTestAgent(prev=>({...prev,id:e.target.value}))}
                     style={{ padding: 6, fontFamily: 'monospace' }} />
              <input placeholder="spawn x" type="number" value={testAgent.position.x}
                     onChange={e=>setTestAgent(prev=>({...prev,position:{...prev.position,x:Number(e.target.value)||0}}))}
                     style={{ padding: 6, fontFamily: 'monospace' }} />
              <input placeholder="spawn y" type="number" value={testAgent.position.y}
                     onChange={e=>setTestAgent(prev=>({...prev,position:{...prev.position,y:Number(e.target.value)||0}}))}
                     style={{ padding: 6, fontFamily: 'monospace' }} />
              <input placeholder="color" value={testAgent.color}
                     onChange={e=>setTestAgent(prev=>({...prev,color:e.target.value}))}
                     style={{ padding: 6, fontFamily: 'monospace' }} />
              <input placeholder="agentType (e.g. warrior)" value={testAgent.agentType}
                     onChange={e=>setTestAgent(prev=>({...prev,agentType:e.target.value}))}
                     style={{ padding: 6, fontFamily: 'monospace' }} />
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <button style={{...smallButtonStyle, backgroundColor:'#4CAF50'}}
                onClick={async()=>{
                  try {
                    if (engineMode) {
                      const effectiveId = testAgent.id.trim();
                      const pos = { ...testAgent.position };
                      const req:any = { agentId: effectiveId, position: pos, agentType: testAgent.agentType, color: testAgent.color, attributes: { health: 25, strength: 25, defense: 25, speed: 25 } };
                      const ack:any = await webSocketService.emit('dev:agent:register', req);
                      if (!ack?.success) { addLog('Dev','emit',`dev:agent:register failed: ${ack?.error||'unknown'}`,'error', effectiveId); return; }
                      const id2 = String(ack?.agentId || effectiveId);
                      setTestAgent(prev => ({ ...prev, id: id2 }));
                      setLastAgentId(id2);
                      addLog('Dev','emit',`dev:agent:register ok agentId=${id2}`,'success', id2);
                    } else {
                      const data = { agent: { id: testAgent.id, position: testAgent.position, color: testAgent.color, health: testAgent.health } };
                      await webSocketService.emit('dev:inject', { eventType: 'agent:spawned', data });
                      addLog('Dev', 'emit', `agent:spawned ${JSON.stringify(data)}`,'success', testAgent.id);
                    }
                  } catch (e:any) {
                    addLog('Dev', 'emit', `agent spawn failed ${e?.message||e}`,'error');
                  }
                }}>Spawn agent (dev)</button>
              <button style={{...smallButtonStyle, backgroundColor:'#2196F3'}}
                onClick={async()=>{
                  const to = { x: testAgent.position.x+1, y: testAgent.position.y };
                  try {
                    if (engineMode) {
                      const aid = lastAgentId || testAgent.id;
                      const data = { agentId: aid, toPosition: to };
                      await webSocketService.emit('dev:map:moveAgent', data);
                      addLog('Dev', 'emit', `dev:map:moveAgent ${JSON.stringify(data)}`,'success', aid);
                    } else {
                      const data = { agentId: testAgent.id, fromPosition: testAgent.position, toPosition: to };
                      await webSocketService.emit('dev:inject', { eventType: 'agent:moved', data });
                      addLog('Dev', 'emit', `agent:moved ${JSON.stringify(data)}`,'success', testAgent.id);
                    }
                  } catch (e:any) {
                    addLog('Dev', 'emit', `agent move failed ${e?.message||e}`,'error');
                  }
                }}>Move agent (+1,0)</button>
            </div>

            {/* Agent move to specific tile */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6, marginBottom:8 }}>
              <input id="moveX" placeholder="move to x (grid)" type="number" style={{ padding:6, fontFamily:'monospace' }} />
              <input id="moveY" placeholder="move to y (grid)" type="number" style={{ padding:6, fontFamily:'monospace' }} />
              <button style={{...smallButtonStyle, backgroundColor:'#1E88E5'}} onClick={async()=>{
                const mx = Number((document.getElementById('moveX') as HTMLInputElement)?.value);
                const my = Number((document.getElementById('moveY') as HTMLInputElement)?.value);
                if (Number.isNaN(mx) || Number.isNaN(my)) { addLog('Dev','move','目标坐标无效','error'); return; }
                try {
                  if (engineMode) {
                    const data = { agentId: testAgent.id, toPosition: { x: mx, y: my } };
                    await webSocketService.emit('dev:map:moveAgent', data);
                    addLog('Dev','emit',`dev:map:moveAgent ${JSON.stringify(data)}`,'success');
                  } else {
                    const data = { agentId: testAgent.id, fromPosition: testAgent.position, toPosition: { x: mx, y: my } };
                    await webSocketService.emit('dev:inject', { eventType: 'agent:moved', data });
                    addLog('Dev','emit',`agent:moved ${JSON.stringify(data)}`,'success');
                  }
                } catch(e:any){ addLog('Dev','emit',`agent:moved failed ${e?.message||e}`,'error'); }
              }}>Move to (x,y)</button>
            </div>

            {/* Health controls (test-only) */}
            <div style={{ display:'grid', gridTemplateColumns:'auto 1fr auto', gap:6, marginBottom:8, alignItems:'center' }}>
              <label style={{ fontSize:12 }}>Set HP (current/max):</label>
              <div style={{ display:'flex', gap:6 }}>
                <input id="hpCurrent" placeholder="current" type="number" defaultValue={20}
                       style={{ padding:6, fontFamily:'monospace', width:100 }} />
                <input id="hpMax" placeholder="max" type="number" defaultValue={100}
                       style={{ padding:6, fontFamily:'monospace', width:100 }} />
              </div>
              <button style={{...smallButtonStyle, backgroundColor:'#D35400'}}
                onClick={async()=>{
                  const c = Number((document.getElementById('hpCurrent') as HTMLInputElement)?.value);
                  const m = Number((document.getElementById('hpMax') as HTMLInputElement)?.value);
                  if (!engineMode) { addLog('Health','note','SetHealth 仅在 8081 引擎模式生效','warning'); return; }
                  const aid = lastAgentId || testAgent.id;
                  if (!aid) { addLog('Health','emit','no agentId available','error'); return; }
                  try { await webSocketService.emit('dev:agent:setHealth', { agentId: aid, current: c, max: m }); addLog('Health','emit',`setHealth(${aid}) to ${c}/${m}`,'success', aid); }
                  catch(e:any){ addLog('Health','emit',`setHealth failed ${e?.message||e}`,'error', aid); }
                }}>Apply</button>
            </div>

            {/* Resource controls (authoritative) */}
            <div style={{ display:'flex', gap:6, marginBottom:8 }}>
              <button style={{...smallButtonStyle, backgroundColor:'#2ECC71'}}
                onClick={async()=>{
                  const tile = { x: testAgent.position.x+1, y: testAgent.position.y };
                  try {
                    if (engineMode) {
                      const payload = { items: [{ type: 'WEAPON', position: tile, value: 50, metadata: { weaponType: weaponSelect } }], currentTurn: 0 };
                      const ack:any = await webSocketService.emit('dev:resource:spawn', payload);
                      const spawned = ack?.items?.[0];
                      if (spawned?.itemId) {
                        addLog('Dev','emit',`dev:resource:spawn ✅ ${JSON.stringify(spawned)}`,'success');
                        lastSpawnRef.current = { itemId: spawned.itemId, category: 'weapon', weaponType: 'SWORD', pos: { x: tile.x, y: tile.y } } as any;
                      } else {
                        addLog('Dev','emit',`dev:resource:spawn ⚠️ no itemId in ACK`,'warning');
                      }
                    } else {
                      const payload = { items: [{ type: 'WEAPON', position: tile, value: 50, metadata: { weaponType: weaponSelect } }], currentTurn: 0 };
                      const ack:any = await webSocketService.emit('resource:spawnRequest', payload);
                      const spawned = ack?.items?.[0];
                      if (spawned?.itemId) {
                        addLog('Dev','emit',`resource:spawnRequest ✅ ${JSON.stringify(spawned)}`,'success');
                        lastSpawnRef.current = { itemId: spawned.itemId, category: 'weapon', weaponType: 'SWORD', pos: { x: tile.x, y: tile.y } } as any;
                      } else {
                        addLog('Dev','emit',`resource:spawnRequest ⚠️ no itemId in ACK`,'warning');
                      }
                    }
                  } catch (e:any) {
                    addLog('Dev','emit',`spawn failed ${e?.message||e}`,'error');
                  }
                }}>Spawn weapon (auth)</button>
              <button style={{...smallButtonStyle, backgroundColor:'#27AE60'}}
                onClick={async()=>{
                  try {
                    if (engineMode) {
                      const payload = { agentId: testAgent.id, currentTurn: 0 };
                      // 优先使用带入库存的收集（测试闭环）：collectStore
                      const ack:any = await webSocketService.emit('dev:resource:collectStore', payload);
                      addLog('Dev','emit',`dev:resource:collect ✅ ${JSON.stringify(ack)}`,'success');
                    } else {
                      const payload = { agentId: testAgent.id, currentTurn: 0 };
                      const ack:any = await webSocketService.emit('resource:collectRequest', payload);
                      addLog('Dev','emit',`collect by agent tile ✅ ${JSON.stringify(ack)}`,'success');
                    }
                  } catch (e:any) {
                    addLog('Dev','emit',`collect failed ${e?.message||e}`,'error');
                  }
                }}>Collect at agent tile (auth)</button>
              <button style={{...smallButtonStyle, backgroundColor:'#9B59B6'}}
                onClick={async()=>{
                  const data = { agentId: testAgent.id, weaponType: 'SWORD' };
                  try {
                    await webSocketService.emit('dev:inject', { eventType: 'weapon:equipped', data });
                    addLog('Dev','emit',`weapon:equipped ${JSON.stringify(data)}`,'success');
                  } catch (e:any) {
                    addLog('Dev','emit',`weapon:equipped failed (no ACK?) ${e?.message||e}`,'error');
                  }
                }}>Equip SWORD</button>
              <label style={{ fontSize:12, alignSelf:'center' }}>Medical:</label>
              <select value={medicalSelect} onChange={e=>setMedicalSelect(e.target.value as any)} style={{ padding:6, fontFamily:'monospace' }}>
                <option value="large">large</option>
                <option value="small">small</option>
              </select>
              <button style={{...smallButtonStyle, backgroundColor:'#8E44AD'}}
                onClick={async()=>{
                  const tile = { x: testAgent.position.x, y: testAgent.position.y };
                  try {
                    if (engineMode) {
                      const payload = { items: [{ type: 'MEDICAL', position: tile, value: medicalSelect==='large'?50:15, metadata: { medicalType: medicalSelect } }], currentTurn: 0 };
                      const ack:any = await webSocketService.emit('dev:resource:spawn', payload);
                      addLog('Dev','emit',`dev:resource:spawn (medical) ✅ ${JSON.stringify(ack)}`,'success');
                    } else {
                      const payload = { items: [{ type: 'MEDICAL', position: tile, value: medicalSelect==='large'?50:15, metadata: { medicalType: medicalSelect } }], currentTurn: 0 };
                      const ack:any = await webSocketService.emit('resource:spawnRequest', payload);
                      addLog('Dev','emit',`resource:spawnRequest (medical) ✅ ${JSON.stringify(ack)}`,'success');
                    }
                  } catch (e:any) {
                    addLog('Dev','emit',`spawn medical failed ${e?.message||e}`,'error');
                  }
                }}>Spawn medical (auth)</button>
              <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <select value={selectedMedicalId} onChange={e=>setSelectedMedicalId(e.target.value)} style={{ padding:6, fontFamily:'monospace', minWidth: 180 }}>
                  <option value="">-- select medical itemId --</option>
                  {medicalIds.map(id => (<option key={id} value={id}>{id}</option>))}
                </select>
                <button style={{...smallButtonStyle, backgroundColor:'#16A085'}}
                  onClick={async()=>{
                    try {
                      if (!selectedMedicalId) { addLog('UseItem','precheck','请选择一个medical itemId','warning'); return; }
                      if (engineMode) {
                        await webSocketService.emit('dev:agent:setHealth', { agentId: testAgent.id, current: 20, max: 100 });
                        await webSocketService.emit('dev:item:use', { agentId: testAgent.id, itemId: selectedMedicalId });
                        addLog('UseItem','emit',`dev:item:use itemId=${selectedMedicalId}`,'success');
                      } else {
                        addLog('UseItem','note','非引擎模式暂不支持use命令，请切到8081','warning');
                      }
                    } catch (e:any) {
                      addLog('UseItem','failed',e?.message||String(e),'error');
                    }
                  }}>Use selected medical (auth)</button>
              </div>
            </div>

            {/* Combat controls */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, marginBottom:8 }}>
              <input placeholder="attackerId" defaultValue={combatAgents.attacker.id} id="attackerId"
                     style={{ padding:6, fontFamily:'monospace' }} />
              <input placeholder="defenderId" defaultValue={combatAgents.defender.id} id="defenderId"
                     style={{ padding:6, fontFamily:'monospace' }} />
            </div>
            <div style={{ display:'flex', gap:6 }}>
              <button style={{...smallButtonStyle, backgroundColor:'#E67E22'}}
                onClick={async()=>{
                  const attackerId = (document.getElementById('attackerId') as HTMLInputElement)?.value || combatAgents.attacker.id;
                  const defenderId = (document.getElementById('defenderId') as HTMLInputElement)?.value || combatAgents.defender.id;
                  const data = { combatId: 'combat_'+Date.now(), attackerId, defenderId, theater: { enabled:true } };
                  try {
                    if (engineMode) await webSocketService.emit('dev:combat:started', data);
                    else await webSocketService.emit('dev:inject', { eventType: 'combat:started', data });
                    addLog('Dev','emit',`combat:started ${JSON.stringify(data)}`,'success');
                  } catch (e:any) {
                    addLog('Dev','emit',`combat:started failed (no ACK?) ${e?.message||e}`,'error');
                  }
                }}>Start combat</button>
              <button style={{...smallButtonStyle, backgroundColor:'#C0392B'}}
                onClick={async()=>{
                  const attackerId = (document.getElementById('attackerId') as HTMLInputElement)?.value || combatAgents.attacker.id;
                  const defenderId = (document.getElementById('defenderId') as HTMLInputElement)?.value || combatAgents.defender.id;
                  const data = { combatId: 'combat_tmp', attackerId, defenderId, damage: 15, isCritical: false };
                  try {
                    if (engineMode) await webSocketService.emit('dev:combat:damageDealt', data);
                    else await webSocketService.emit('dev:inject', { eventType: 'combat:damageDealt', data });
                    addLog('Dev','emit',`combat:damageDealt ${JSON.stringify(data)}`,'success');
                  } catch (e:any) {
                    addLog('Dev','emit',`combat:damageDealt failed (no ACK?) ${e?.message||e}`,'error');
                  }
                }}>Damage dealt</button>
              <button style={{...smallButtonStyle, backgroundColor:'#7F8C8D'}}
                onClick={async()=>{
                  const attackerId = (document.getElementById('attackerId') as HTMLInputElement)?.value || combatAgents.attacker.id;
                  const defenderId = (document.getElementById('defenderId') as HTMLInputElement)?.value || combatAgents.defender.id;
                  const data = { combatId: 'combat_tmp', attackerId, defenderId };
                  try {
                    if (engineMode) await webSocketService.emit('dev:combat:attackMissed', data);
                    else await webSocketService.emit('dev:inject', { eventType: 'combat:attackMissed', data });
                    addLog('Dev','emit',`combat:attackMissed ${JSON.stringify(data)}`,'success');
                  } catch (e:any) {
                    addLog('Dev','emit',`combat:attackMissed failed (no ACK?) ${e?.message||e}`,'error');
                  }
                }}>Attack missed</button>
              <button style={{...smallButtonStyle, backgroundColor:'#16A085'}}
                onClick={async()=>{
                  const attackerId = (document.getElementById('attackerId') as HTMLInputElement)?.value || combatAgents.attacker.id;
                  const defenderId = (document.getElementById('defenderId') as HTMLInputElement)?.value || combatAgents.defender.id;
                  const data = { combatId: 'combat_tmp', winner: attackerId, loser: defenderId, reason: 'health_depleted' };
                  try {
                    if (engineMode) await webSocketService.emit('dev:combat:ended', data);
                    else await webSocketService.emit('dev:inject', { eventType: 'combat:ended', data });
                    addLog('Dev','emit',`combat:ended ${JSON.stringify(data)}`,'success');
                  } catch (e:any) {
                    addLog('Dev','emit',`combat:ended failed (no ACK?) ${e?.message||e}`,'error');
                  }
                }}>End combat</button>
            </div>
          </div>

          {/* Inventory (read-only) */}
          <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #ccc', borderRadius: '5px' }}>
            <h3>🎒 Inventory (read-only)</h3>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px' }}>基于生产事件实时汇总，不影响逻辑</div>
            {Object.keys(inventory).length === 0 ? (
              <div style={{ fontSize: '12px', color: '#999' }}>暂无数据。按上面按钮触发生产事件以观察。</div>
            ) : (
              <div style={{ maxHeight: 180, overflowY: 'auto', fontSize: '12px' }}>
                {Object.entries(inventory).map(([aid, inv]) => (
                  <div key={aid} style={{ padding: '6px 0', borderBottom: '1px dashed #333' }}>
                    <div><strong>{aid}</strong></div>
                    <div>has weapon: {(inv.weapons && inv.weapons.length > 0) ? 'YES' : 'NO'}</div>
                    <div>weapons: {inv.weapons.join(', ') || '—'}</div>
                    <div>medical: large={inv.medical.large} small={inv.medical.small}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #ccc', borderRadius: '5px' }}>
            <h3>🎮 Camera Controls</h3>
            <div style={{ fontSize: '11px', fontFamily: 'monospace', marginBottom: '8px' }}>
              <div>Position: ({cameraControls.x.toFixed(0)}, {cameraControls.y.toFixed(0)})</div>
              <div>Zoom: {cameraControls.zoom.toFixed(2)}x</div>
            </div>

            {/* 方向键控制 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '3px', marginBottom: '8px' }}>
              <div></div>
              <button onClick={() => moveCamera(0, -100)} style={smallButtonStyle}>↑</button>
              <div></div>
              <button onClick={() => moveCamera(-100, 0)} style={smallButtonStyle}>←</button>
              <button onClick={resetCamera} style={{...smallButtonStyle, backgroundColor: '#95a5a6'}}>⌂</button>
              <button onClick={() => moveCamera(100, 0)} style={smallButtonStyle}>→</button>
              <div></div>
              <button onClick={() => moveCamera(0, 100)} style={smallButtonStyle}>↓</button>
              <div></div>
            </div>

            {/* 缩放控制 */}
            <div style={{ display: 'flex', gap: '3px', marginBottom: '8px' }}>
              <button onClick={() => zoomCamera(-0.2)} style={{...smallButtonStyle, flex: 1}}>Zoom -</button>
              <button onClick={() => zoomCamera(0.2)} style={{...smallButtonStyle, flex: 1}}>Zoom +</button>
            </div>

            <button
              onClick={centerOnAgent}
              style={{...buttonStyle, backgroundColor: '#9B59B6', fontSize: '12px', padding: '8px'}}
              disabled={!gameReady}
            >
              Center on Agent
            </button>

            <div style={{ fontSize: '10px', color: '#666', marginTop: '8px' }}>
              <div><strong>键盘快捷键:</strong></div>
              <div>WASD/方向键: 移动相机</div>
              <div>Q/E: 缩放</div>
              <div>R: 重置相机</div>
              <div>F: 居中Agent</div>
            </div>
          </div>

          <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #ccc', borderRadius: '5px' }}>
            <h3>Active Agent</h3>
            <div style={{ fontSize: '11px', fontFamily: 'monospace', marginBottom: '6px' }}>
              <div>Last ID: {lastAgentId ?? '—'}</div>
              <div>Last Pos: ({testAgent.position.x}, {testAgent.position.y})</div>
            </div>
            <button
              onClick={centerOnAgent}
              style={{...buttonStyle, backgroundColor: '#9B59B6', fontSize: '12px', padding: '8px'}}
              disabled={!gameReady || !lastAgentId}
            >
              Center on Last Agent
            </button>
          </div>

          {/* All local test buttons removed in backend mode */}

          <button
            onClick={clearLogs}
            style={{...buttonStyle, backgroundColor: '#E74C3C', marginTop: '8px'}}
          >
            Clear Logs
          </button>

          {/* Reset combat state not applicable in backend mode */}

          {/* Expected Chain Flow */}
          <div style={{ marginTop: '20px', fontSize: '11px', color: '#666' }}>
            <h3>🔍 Expected Chain Flow (Backend-Driven):</h3>
            <div style={{ marginBottom: '10px' }}>
              <strong>AGENT_SPAWNED (Backend-Driven):</strong>
              <ol style={{ paddingLeft: '15px', marginTop: '5px' }}>
                <li>Backend Socket.IO event</li>
                <li>BattleScene.handleAgentSpawnedEvent() → Engine.handle()</li>
                <li>🔧 SinglePathAnimationEngine: priority queue (SPAWNED=5)</li>
                <li>🔧 Engine: Scene layer ready check + priority gate</li>
                <li>🔧 SceneAdapter.idle({'{spawn: true, gridX, gridY}'})</li>
                <li>🔧 SceneAdapter → PhaserAnimationManager.addAgent()</li>
                <li>🔧 PhaserAnimationManager.playAnimation('idle')</li>
                <li>✅ Visual: Agent appears with idle animation @10fps</li>
              </ol>
            </div>
            <div>
              <strong>AGENT_MOVED (Backend LLM Path):</strong>
              <ol style={{ paddingLeft: '15px', marginTop: '5px' }}>
                <li>Backend LLM path planner</li>
                <li>For each step: AGENT_MOVED (from backend)</li>
                <li>BattleScene → SinglePathAnimationEngine.handle()</li>
                <li>🔧 Engine: AGENT_MOVED priority=4, movement queue</li>
                <li>🔧 SceneAdapter.walk(onComplete) → PhaserAnimationManager</li>
                <li>🔧 MovementAnimationSync: 300ms jumpDuration + walk@16fps</li>
                <li>🔧 Movement complete → Engine.finish() → next queued event</li>
                <li>✅ Visual: Agent tile-by-tile movement with proper timing</li>
              </ol>
            </div>
            <div>
              <strong>⚔️ COMBAT TEST (Events 4-7):</strong>
              <ol style={{ paddingLeft: '15px', marginTop: '5px' }}>
                <li>Step 1: Spawn 2 agents → AGENT_SPAWNED + WEAPON_EQUIPPED</li>
                <li>Step 2: Move adjacent → AGENT_MOVED to adjacent tile</li>
                <li>Step 3: Combat start → COMBAT_STARTED (Theater opens)</li>
                <li>Turn-based loop: COMBAT_DAMAGE_DEALT/MISSED events</li>
                <li>Combat end → COMBAT_ENDED (Theater closes)</li>
                <li>✅ Visual: Theater mode + layered combat animations</li>
              </ol>
            </div>
            <div style={{ marginTop: '8px', color: '#E74C3C', fontSize: '10px' }}>
              <strong>🔧 Post-Migration Changes:</strong>
              <div>• BattleScene direct processing removed</div>
              <div>• Engine priority queue with stepInterval control</div>
              <div>• SceneAdapter spawn parameters support</div>
              <div>• System-controlled timing (no test hardcoding)</div>
              <div>• Combat events route directly to Engine (priority 1-2)</div>
            </div>
          </div>
        </div>

        {/* Real-time Chain Logs */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h2>📋 Live Event & Animation Logs</h2>

          {/* Phaser Game Container */}
          <div style={{ marginBottom: '20px' }}>
            <h3>🎬 Phaser Game Canvas</h3>
            <div
              id="phaser-game-container"
              style={{
                border: '2px solid #34495e',
                borderRadius: '5px',
                width: '1200px',
                height: '800px',
                overflow: 'hidden'
              }}
            />
          </div>

          {/* Chain Execution Logs */}
          <div style={{
            flex: 1,
            border: '1px solid #ccc',
            padding: '10px',
            backgroundColor: '#1e1e1e',
            color: '#fff',
            fontSize: '12px',
            overflowY: 'auto',
            borderRadius: '5px'
          }}>
            {logs.map((log, index) => (
              <div key={index} style={{
                marginBottom: '5px',
                padding: '8px',
                borderLeft: `3px solid ${log.status === 'success' ? '#90EE90' : log.status === 'error' ? '#FF6B6B' : '#87CEEB'}`,
                backgroundColor: log.status === 'success' ? 'rgba(0,255,0,0.1)' : log.status === 'error' ? 'rgba(255,0,0,0.1)' : 'rgba(135,206,235,0.1)',
                borderRadius: '3px'
              }}>
                <div style={{ fontSize: '10px', color: '#888', marginBottom: '2px' }}>
                  [{log.timestamp}] {log.module}.{log.method}
                  {log.agentId && ` → ${log.agentId}`}
                </div>
                <div style={{ color: '#fff', whiteSpace: 'pre-wrap' }}>
                  {log.details}
                </div>
              </div>
            ))}
            {logs.length === 0 && (
              <div style={{ color: '#888', fontStyle: 'italic' }}>
                Waiting for backend events…
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentSpawnedRealTest;
